import React, { useState, useEffect } from 'react';
import { FiImage, FiUpload, FiPlay, FiPause, FiLoader, FiScissors } from 'react-icons/fi';
import { adminAPI, API_ORIGIN } from '../../../../services/api';
import { parseSentencesFromFullText } from '../../../../utils/lessonTextSentences';

const TextBlockEditor = ({ block, onUpdate, availableVoices = [], setError, setSuccess }) => {
    const [textData, setTextData] = useState({
        textTitle: block.textTitle || '',
        textDescription: block.textDescription || '',
        fullTextEn: block.fullTextEn || '',
        fullTextRu: block.fullTextRu || '',
        textImageUrl: block.textImageUrl || '',
        textVoiceId: block.textVoiceId || 'en-US-JennyNeural',
        textAudioType: block.textAudioType || 'auto',
        textAudioUrl: block.textAudioUrl || '',
        textSentences: block.textSentences || []
    });
    
    const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
    const [previewAudioUrl, setPreviewAudioUrl] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = React.useRef(null);
    const [showSlicer, setShowSlicer] = useState(false);

    const handleChange = (field, value) => {
        const updated = { ...textData, [field]: value };
        setTextData(updated);
        onUpdate(updated);
    };

    const splitTextToSentences = () => {
        if (!textData.fullTextEn) {
            setError('Сначала введите текст');
            return;
        }

        const sentencesEn = parseSentencesFromFullText(textData.fullTextEn);
        const sentencesRu = parseSentencesFromFullText(textData.fullTextRu || '');

        const parsed = sentencesEn.map((enSentence, idx) => {
            const ruSentence = sentencesRu[idx] || '';
            const enParts = enSentence.split('|').map(p => p.trim()).filter(p => p);
            const ruParts = ruSentence.split('|').map(p => p.trim()).filter(p => p);

            return {
                en: enSentence.trim(),
                ru: ruSentence.trim(),
                parts: enParts.length > 1 ? enParts : [],
                partsRu: ruParts.length > 1 ? ruParts : [],
                partsValid: enParts.length === 0 || (enParts.length === ruParts.length),
                audioUrl: ''
            };
        });

        handleChange('textSentences', parsed);
        setSuccess(`Текст разбит на ${parsed.length} предложений`);
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !file.type.startsWith('image/')) {
            setError('Выберите файл изображения');
            return;
        }

        if (file.size > 8 * 1024 * 1024) {
            setError('Размер файла не должен превышать 8 МБ');
            return;
        }

        try {
            const formData = new FormData();
            formData.append('image', file);
            const response = await adminAPI.uploadTextImage(formData);
            handleChange('textImageUrl', response.data.imageUrl);
            setSuccess('Изображение загружено');
        } catch (err) {
            setError('Ошибка загрузки изображения');
        }
    };

    const handleAudioUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !file.type.startsWith('audio/')) {
            setError('Выберите аудио файл');
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            setError('Размер файла не должен превышать 10MB');
            return;
        }

        try {
            const formData = new FormData();
            formData.append('audio', file);
            const response = await adminAPI.uploadTextAudio(formData);
            handleChange('textAudioUrl', response.data.audioUrl);
            setSuccess('Аудио загружено');
        } catch (err) {
            setError('Ошибка загрузки аудио');
        }
    };
    
    const generatePreviewAudio = async () => {
        if (!textData.fullTextEn) {
            setError('Сначала введите текст');
            return;
        }
        
        setIsGeneratingAudio(true);
        setIsPlaying(false);
        
        try {
            const response = await adminAPI.generateTTS({
                text: textData.fullTextEn,
                voiceId: textData.textVoiceId
            });
            
            setPreviewAudioUrl(response.data.audioUrl);
            setSuccess('Аудио сгенерировано');
            
            // Автоматически начинаем воспроизведение
            setTimeout(() => {
                if (audioRef.current) {
                    audioRef.current.play();
                    setIsPlaying(true);
                }
            }, 100);
        } catch (err) {
            setError('Ошибка генерации аудио');
        } finally {
            setIsGeneratingAudio(false);
        }
    };
    
    const togglePlayPause = () => {
        if (!audioRef.current) return;
        
        if (isPlaying) {
            audioRef.current.pause();
            setIsPlaying(false);
        } else {
            audioRef.current.play();
            setIsPlaying(true);
        }
    };

    return (
        <div style={{ display: 'grid', gap: '20px' }}>
            {/* Название текста */}
            <div>
                <label className="form-label">Название</label>
                <input
                    type="text"
                    className="form-input"
                    placeholder="Название текста"
                    value={textData.textTitle}
                    onChange={(e) => handleChange('textTitle', e.target.value)}
                />
            </div>

            {/* Описание */}
            <div>
                <label className="form-label">Описание (необязательно)</label>
                <textarea
                    className="form-input"
                    placeholder="Краткое описание текста..."
                    value={textData.textDescription}
                    onChange={(e) => handleChange('textDescription', e.target.value)}
                    rows={3}
                />
            </div>

            {/* Изображение и озвучка */}
            <div className="text-image-audio-container" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                    <label className="form-label">Изображение (необязательно)</label>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        style={{ display: 'none' }}
                        id={`image-upload-${block.id}`}
                    />
                    <label
                        htmlFor={`image-upload-${block.id}`}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '10px 16px',
                            background: textData.textImageUrl ? '#52c41a' : '#4a90e2',
                            color: 'white',
                            borderRadius: '6px',
                            cursor: 'pointer'
                        }}
                    >
                        <FiImage /> {textData.textImageUrl ? '✓ Изображение загружено' : 'Загрузить изображение'}
                    </label>
                    {textData.textImageUrl && (
                        <div style={{ marginTop: '10px' }}>
                            <img
                                src={`${API_ORIGIN}${textData.textImageUrl}`}
                                alt="Preview"
                                style={{ maxWidth: '300px', borderRadius: '8px' }}
                            />
                        </div>
                    )}
                </div>

                <div>
                    <label className="form-label">Озвучка текста</label>
                    <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer' }}>
                            <input
                                type="radio"
                                name="text-audio-type"
                                checked={textData.textAudioType === 'auto'}
                                onChange={() => handleChange('textAudioType', 'auto')}
                            />
                            <span style={{ fontSize: '14px' }}>Автоматическая озвучка (TTS)</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <input
                                type="radio"
                                name="text-audio-type"
                                checked={textData.textAudioType === 'custom'}
                                onChange={() => handleChange('textAudioType', 'custom')}
                            />
                            <span style={{ fontSize: '14px' }}>Загрузить свое аудио</span>
                        </label>
                    </div>

                    {textData.textAudioType === 'auto' ? (
                        <>
                            <div>
                                <label className="form-label" style={{ fontSize: '13px', marginBottom: '6px' }}>
                                    Выбор голоса
                                </label>
                                <select
                                    className="form-input"
                                    value={textData.textVoiceId}
                                    onChange={(e) => handleChange('textVoiceId', e.target.value)}
                                >
                                    {availableVoices && availableVoices.length > 0 ? (
                                        availableVoices.filter(v => v.ShortName && v.DisplayName).map(voice => (
                                            <option key={voice.ShortName} value={voice.ShortName}>
                                                {voice.DisplayName}
                                            </option>
                                        ))
                                    ) : (
                                        <option value="en-US-JennyNeural">Jenny (США, женский)</option>
                                    )}
                                </select>
                            </div>
                            
                            {/* Настройка паузы между предложениями */}
                            <div style={{ marginTop: '15px' }}>
                                <label className="form-label" style={{ fontSize: '13px', marginBottom: '6px' }}>
                                    Пауза между предложениями (секунды)
                                </label>
                                <input
                                    type="number"
                                    className="form-input"
                                    min="0"
                                    max="10"
                                    step="0.5"
                                    value={textData.pauseBetweenSentences || 1}
                                    onChange={(e) => handleChange('pauseBetweenSentences', parseFloat(e.target.value))}
                                    placeholder="1"
                                    style={{ width: '150px' }}
                                />
                                <p style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                                    Рекомендуется 1-2 секунды
                                </p>
                            </div>
                            
                            {/* Кнопка прослушивания с play/pause */}
                            <div style={{ marginTop: '15px' }}>
                                <button
                                    type="button"
                                    onClick={previewAudioUrl ? togglePlayPause : generatePreviewAudio}
                                    disabled={!textData.fullTextEn || isGeneratingAudio}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '10px 16px',
                                        background: !textData.fullTextEn || isGeneratingAudio ? '#ccc' : '#52c41a',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: !textData.fullTextEn || isGeneratingAudio ? 'not-allowed' : 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}
                                >
                                    {isGeneratingAudio ? (
                                        <>
                                            <FiLoader className="spinner" /> Загрузка...
                                        </>
                                    ) : isPlaying ? (
                                        <>
                                            <FiPause /> Пауза
                                        </>
                                    ) : (
                                        <>
                                            <FiPlay /> Прослушать текст
                                        </>
                                    )}
                                </button>
                                <p style={{ fontSize: '12px', color: '#999', marginTop: '6px' }}>
                                    {previewAudioUrl ? 'Нажмите для воспроизведения/паузы' : 'Создаёт пробное аудио для прослушивания'}
                                </p>
                            </div>
                            
                            {/* Скрытый аудио элемент */}
                            {previewAudioUrl && (
                                <audio
                                    ref={audioRef}
                                    src={`${API_ORIGIN}${previewAudioUrl}`}
                                    onEnded={() => setIsPlaying(false)}
                                    onPause={() => setIsPlaying(false)}
                                    onPlay={() => setIsPlaying(true)}
                                    style={{ display: 'none' }}
                                />
                            )}
                        </>
                    ) : (
                        <div>
                            <input
                                type="file"
                                accept="audio/*"
                                onChange={handleAudioUpload}
                                style={{ display: 'none' }}
                                id={`audio-upload-${block.id}`}
                            />
                            <label
                                htmlFor={`audio-upload-${block.id}`}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '10px 16px',
                                    background: textData.textAudioUrl ? '#52c41a' : '#4a90e2',
                                    color: 'white',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                <FiUpload /> {textData.textAudioUrl ? '✓ Аудио загружено' : 'Загрузить аудио файл'}
                            </label>
                            {textData.textAudioUrl && (
                                <button
                                    type="button"
                                    onClick={() => handleChange('textAudioUrl', '')}
                                    style={{
                                        marginLeft: '10px',
                                        padding: '10px 16px',
                                        background: 'transparent',
                                        color: '#ff4d4f',
                                        border: '1px solid #ff4d4f',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '13px'
                                    }}
                                >
                                    Удалить
                                </button>
                            )}
                        </div>
                    )}
                </div>
                
                {/* Прослушивание текста и нарезка */}
                {textData.textAudioUrl && (
                    <div style={{ marginTop: '15px' }}>
                        <label className="form-label" style={{ fontSize: '13px', marginBottom: '6px' }}>
                            Прослушать текст
                        </label>
                        <audio
                            controls
                            style={{
                                width: '100%',
                                maxWidth: '400px',
                                height: '40px'
                            }}
                        >
                            <source src={`${API_ORIGIN}${textData.textAudioUrl}`} type="audio/mpeg" />
                            Ваш браузер не поддерживает аудио элемент.
                        </audio>
                        {textData.textSentences && textData.textSentences.length > 0 && (
                            <button
                                type="button"
                                onClick={() => setShowSlicer(true)}
                                style={{
                                    marginTop: '10px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    border: '1px solid #4a90e2',
                                    background: 'white',
                                    color: '#4a90e2',
                                    cursor: 'pointer',
                                    fontSize: '13px'
                                }}
                            >
                                <FiScissors /> Нарезать аудио по предложениям
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Текст на английском */}
            <div>
                <label className="form-label">Текст на английском</label>
                <textarea
                    className="form-input"
                    placeholder="Введите полный текст на английском языке..."
                    value={textData.fullTextEn}
                    onChange={(e) => handleChange('fullTextEn', e.target.value)}
                    rows={8}
                />
                <p style={{ fontSize: '12px', color: '#999', marginTop: '6px' }}>
                    <strong>|</strong> — части одного предложения (для нарезки аудио): «I am going | to the store».
                    {' '}
                    <strong>Слэш с пробелами</strong> (<code> / </code>) — у ученика текст идёт <strong>с новой строки</strong> после каждого слэша; после <code>. ! ?</code> то же разбиение даёт отдельные предложения. Перенос строки: ещё и <strong>новая строка в поле</strong>. Ссылки с <code>://</code> по слэшу не режутся.
                </p>
            </div>

            {/* Текст на русском */}
            <div>
                <label className="form-label">Перевод на русский (необязательно)</label>
                <textarea
                    className="form-input"
                    placeholder="Перевод текста на русский язык..."
                    value={textData.fullTextRu}
                    onChange={(e) => handleChange('fullTextRu', e.target.value)}
                    rows={8}
                />
                <p style={{ fontSize: '12px', color: '#999', marginTop: '6px' }}>
                    Такое же количество <code>|</code> и такие же разделители предложений <code> . / </code>, как в английском тексте.
                </p>
            </div>

            {/* Кнопка разбиения на предложения */}
            <div>
                <button
                    type="button"
                    onClick={splitTextToSentences}
                    disabled={!textData.fullTextEn}
                    style={{
                        padding: '10px 20px',
                        background: '#52c41a',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: textData.fullTextEn ? 'pointer' : 'not-allowed',
                        fontWeight: '500'
                    }}
                >
                    Разбить текст на предложения
                </button>
                <p style={{ fontSize: '13px', color: '#666', marginTop: '8px' }}>
                    {textData.textSentences.length > 0 && `Текст разбит на ${textData.textSentences.length} предложений`}
                </p>
            </div>

            {/* Отображение разбитых предложений */}
            {textData.textSentences && textData.textSentences.length > 0 && (
                <div style={{ 
                    marginTop: '20px',
                    padding: '20px',
                    background: '#f9f9f9',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0'
                }}>
                    <h3 style={{ fontSize: '16px', marginBottom: '15px', color: '#2c3e50' }}>
                        Предложения текста ({textData.textSentences.length})
                    </h3>
                    
                    {textData.textSentences.map((sentence, index) => (
                        <div 
                            key={index}
                            style={{
                                padding: '12px',
                                background: 'white',
                                borderRadius: '6px',
                                marginBottom: '10px',
                                border: '1px solid #e8e8e8'
                            }}
                        >
                            <div style={{ marginBottom: '6px' }}>
                                <span style={{ 
                                    fontSize: '11px', 
                                    color: '#999',
                                    fontWeight: '600'
                                }}>
                                    #{index + 1}
                                </span>
                            </div>
                            <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                                <strong>EN:</strong> {sentence.en}
                            </div>
                            {sentence.ru && (
                                <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                                    <strong>RU:</strong> {sentence.ru}
                                </div>
                            )}
                            {sentence.parts && sentence.parts.length > 1 && (
                                <div style={{
                                    marginTop: '8px',
                                    padding: '6px 10px',
                                    background: '#e6f7ff',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    color: '#0050b3',
                                    marginBottom: '8px'
                                }}>
                                    ✓ Разбито на {sentence.parts.length} части: {sentence.parts.join(' | ')}
                                </div>
                            )}
                            
                            {/* Загрузка аудио для предложения - только если выбран режим "Своё аудио" */}
                            {textData.textAudioType === 'custom' && (
                                <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #f0f0f0' }}>
                                    <input
                                        type="file"
                                        accept="audio/*"
                                        onChange={async (e) => {
                                            const file = e.target.files[0];
                                            if (!file) return;
                                            
                                            if (!file.type.startsWith('audio/')) {
                                                setError('Выберите аудио файл');
                                                return;
                                            }
                                            
                                            if (file.size > 10 * 1024 * 1024) {
                                                setError('Размер файла не должен превышать 10MB');
                                                return;
                                            }
                                            
                                            try {
                                                const formData = new FormData();
                                                formData.append('audio', file);
                                                const response = await adminAPI.uploadSentenceAudio(formData);
                                                
                                                const updatedSentences = [...textData.textSentences];
                                                updatedSentences[index] = {
                                                    ...updatedSentences[index],
                                                    audioUrl: response.data.audioUrl
                                                };
                                                handleChange('textSentences', updatedSentences);
                                                setSuccess('Аудио загружено');
                                            } catch (err) {
                                                setError('Ошибка загрузки аудио');
                                            }
                                        }}
                                        style={{ display: 'none' }}
                                        id={`sentence-audio-${block.id}-${index}`}
                                    />
                                    <label
                                        htmlFor={`sentence-audio-${block.id}-${index}`}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            padding: '6px 12px',
                                            background: sentence.audioUrl ? '#52c41a' : '#4a90e2',
                                            color: 'white',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '12px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        <FiUpload size={12} /> {sentence.audioUrl ? '✓ Аудио загружено' : 'Загрузить аудио'}
                                    </label>
                                    {sentence.audioUrl && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const updatedSentences = [...textData.textSentences];
                                                updatedSentences[index] = {
                                                    ...updatedSentences[index],
                                                    audioUrl: ''
                                                };
                                                handleChange('textSentences', updatedSentences);
                                            }}
                                            style={{
                                                marginLeft: '8px',
                                                padding: '6px 12px',
                                                background: 'transparent',
                                                color: '#ff4d4f',
                                                border: '1px solid #ff4d4f',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontSize: '12px'
                                            }}
                                        >
                                            Удалить
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {showSlicer && textData.textAudioUrl && textData.textSentences && textData.textSentences.length > 0 && (
                <TextAudioSlicerModal
                    audioUrl={`${API_ORIGIN}${textData.textAudioUrl}`}
                    rawAudioUrl={textData.textAudioUrl}
                    sentences={textData.textSentences}
                    textId={block.textId}
                    onClose={() => setShowSlicer(false)}
                    onUpdateSentenceAudio={(sentenceIndex, audioUrl, sliceStart, sliceEnd, partIndex) => {
                        const updatedSentences = [...textData.textSentences];
                        const sent = { ...updatedSentences[sentenceIndex] };
                        if (partIndex != null) {
                            const urls = Array.isArray(sent.partAudioUrls) ? [...sent.partAudioUrls] : [];
                            const starts = Array.isArray(sent.partSliceStarts) ? [...sent.partSliceStarts] : [];
                            const ends = Array.isArray(sent.partSliceEnds) ? [...sent.partSliceEnds] : [];
                            urls[partIndex] = audioUrl;
                            starts[partIndex] = sliceStart;
                            ends[partIndex] = sliceEnd;
                            sent.partAudioUrls = urls;
                            sent.partSliceStarts = starts;
                            sent.partSliceEnds = ends;
                        } else {
                            sent.audioUrl = audioUrl;
                            if (sliceStart != null) sent.sliceStart = sliceStart;
                            if (sliceEnd != null) sent.sliceEnd = sliceEnd;
                        }
                        updatedSentences[sentenceIndex] = sent;
                        handleChange('textSentences', updatedSentences);
                    }}
                />
            )}
        </div>
    );
};

// Разворачиваем предложения в список "частей": если у предложения есть parts (разбиение на части), показываем каждую часть отдельно
function buildSlicerItems(sentences) {
    if (!sentences || !sentences.length) return [];
    const items = [];
    sentences.forEach((s, sentenceIndex) => {
        if (s.parts && s.parts.length > 1) {
            s.parts.forEach((partText, partIndex) => {
                items.push({ sentenceIndex, partIndex, text: partText, sentence: s });
            });
        } else {
            items.push({ sentenceIndex, partIndex: null, text: s.en, sentence: s });
        }
    });
    return items;
}

function getStartEndForItem(sentences, itemIndex) {
    const items = buildSlicerItems(sentences);
    const item = items[itemIndex];
    if (!item) return { start: 0, end: 0 };
    const s = item.sentence;
    let start = 0, end = 0;
    if (item.partIndex != null && s.partSliceStarts && s.partSliceEnds && s.partSliceStarts[item.partIndex] != null) {
        start = Number(s.partSliceStarts[item.partIndex]) || 0;
        end = Number(s.partSliceEnds[item.partIndex]) || 0;
    } else if (s.sliceStart != null || s.sliceEnd != null) {
        start = Number(s.sliceStart) || 0;
        end = Number(s.sliceEnd) || 0;
    }
    return { start, end };
}

// Оставляем в поле только цифры и одну точку (удобный ввод секунд)
function formatSecondsInput(value) {
    const v = String(value).replace(',', '.');
    const match = v.match(/^\d*\.?\d*/);
    return match ? match[0] : '';
}

function formatTimeLabel(seconds) {
    if (!Number.isFinite(seconds) || seconds < 0) return '00:00.000';
    const mins = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(3).padStart(6, '0');
    return `${String(mins).padStart(2, '0')}:${secs}`;
}

const TextAudioSlicerModal = ({ audioUrl, rawAudioUrl, sentences, textId, onClose, onUpdateSentenceAudio }) => {
    const items = React.useMemo(() => buildSlicerItems(sentences), [sentences]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [startInput, setStartInput] = useState(() => {
        const { start } = getStartEndForItem(sentences, 0);
        return String(start);
    });
    const [endInput, setEndInput] = useState(() => {
        const { end } = getStartEndForItem(sentences, 0);
        return String(end);
    });
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [playerTime, setPlayerTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const audioRef = React.useRef(null);

    const item = items[currentIndex];
    const sentence = item?.sentence;
    const partIndex = item?.partIndex ?? null;

    useEffect(() => {
        setSaveSuccess(false);
        if (!item) return;
        const { start, end } = getStartEndForItem(sentences, currentIndex);
        setStartInput(String(start));
        setEndInput(String(end));
    }, [currentIndex, sentences]);

    const setStartFromPlayer = () => {
        if (audioRef.current) {
            const t = Number(audioRef.current.currentTime.toFixed(3));
            setStartInput(String(t));
        }
    };

    const setEndFromPlayer = () => {
        if (audioRef.current) {
            const t = Number(audioRef.current.currentTime.toFixed(3));
            setEndInput(String(t));
        }
    };

    const handleTimelineChange = (value) => {
        const nextTime = Number(value);
        setPlayerTime(nextTime);
        if (audioRef.current) {
            audioRef.current.currentTime = nextTime;
        }
    };

    const adjustBoundary = (type, delta) => {
        const sourceValue = type === 'start' ? startInput : endInput;
        const currentValue = parseFloat(sourceValue) || 0;
        const nextValue = Math.max(0, Number((currentValue + delta).toFixed(3)));

        if (type === 'start') {
            setStartInput(String(nextValue));
        } else {
            setEndInput(String(nextValue));
        }
    };

    const handleSaveSlice = async (silent = false) => {
        try {
            if (!sentence) return false;

            const startTime = parseFloat(startInput) || 0;
            const endTime = parseFloat(endInput) || 0;
            if (endTime <= startTime) {
                if (!silent) alert('Конец должен быть больше начала');
                return false;
            }

            setSaving(true);
            const payload = {
                audioUrl: rawAudioUrl || audioUrl.replace(API_ORIGIN, ''),
                startTime,
                endTime
            };
            let response;
            if (textId != null && sentence.id != null && partIndex == null) {
                response = await adminAPI.sliceTextAudioForSentence(textId, sentence.id, payload);
            } else {
                response = await adminAPI.sliceAudio(payload);
            }
            const newUrl = response.data.audioUrl;
            onUpdateSentenceAudio(item.sentenceIndex, newUrl, startTime, endTime, partIndex);
            if (!silent) {
                setSaveSuccess(true);
                setTimeout(() => setSaveSuccess(false), 3000);
            }
            return true;
        } catch (err) {
            console.error('Ошибка нарезки аудио:', err);
            if (!silent) {
                const msg = err.response?.data?.error || 'Ошибка при сохранении фрагмента аудио';
                alert(msg);
            }
            return false;
        } finally {
            setSaving(false);
        }
    };

    const handleNavigation = async (direction) => {
        const startTime = parseFloat(startInput) || 0;
        const endTime = parseFloat(endInput) || 0;
        
        // Автосохранение если изменились границы
        const { start: origStart, end: origEnd } = getStartEndForItem(sentences, currentIndex);
        const hasChanges = startTime !== origStart || endTime !== origEnd;
        
        if (hasChanges && endTime > startTime) {
            await handleSaveSlice(true);
        }
        
        // Переход
        if (direction === 'next' && currentIndex < items.length - 1) {
            // При переходе вперед начало следующего = конец текущего
            const nextIndex = currentIndex + 1;
            setCurrentIndex(nextIndex);
            
            // Устанавливаем начало следующего как конец текущего
            setTimeout(() => {
                setStartInput(String(endTime));
            }, 0);
        } else if (direction === 'prev' && currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    const hasAudio = partIndex != null
        ? (sentence?.partAudioUrls && sentence.partAudioUrls[partIndex])
        : sentence?.audioUrl;

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000
            }}
        >
            <div
                style={{
                    background: 'white',
                    borderRadius: '12px',
                    padding: '20px',
                    width: '800px',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <h3 style={{ margin: 0, fontSize: '18px' }}>Нарезка аудио по предложениям</h3>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            fontSize: '18px'
                        }}
                    >
                        ×
                    </button>
                </div>

                <div style={{ marginBottom: '15px' }}>
                    <audio
                        src={audioUrl}
                        ref={audioRef}
                        controls
                        onTimeUpdate={() => {
                            if (audioRef.current) {
                                setPlayerTime(audioRef.current.currentTime);
                            }
                        }}
                        onLoadedMetadata={() => {
                            if (audioRef.current) {
                                setDuration(audioRef.current.duration || 0);
                                setPlayerTime(audioRef.current.currentTime || 0);
                            }
                        }}
                        style={{ width: '100%' }}
                    />
                </div>

                <div
                    style={{
                        marginBottom: '14px',
                        padding: '12px',
                        background: '#fafafa',
                        border: '1px solid #eee',
                        borderRadius: '8px'
                    }}
                >
                    <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                        Поставьте аудио на паузу в нужном месте или перетащите ползунок, затем отметьте начало или конец.
                    </div>
                    <input
                        type="range"
                        min="0"
                        max={duration || 0}
                        step="0.01"
                        value={Math.min(playerTime, duration || 0)}
                        onChange={(e) => handleTimelineChange(e.target.value)}
                        style={{ width: '100%', marginBottom: '8px' }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666' }}>
                        <span>{formatTimeLabel(playerTime)}</span>
                        <span>{formatTimeLabel(duration)}</span>
                    </div>
                </div>

                {saveSuccess && (
                    <div
                        style={{
                            marginBottom: '12px',
                            padding: '10px 14px',
                            background: '#f6ffed',
                            border: '1px solid #b7eb8f',
                            borderRadius: '8px',
                            color: '#52c41a',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <span style={{ fontSize: '18px' }}>✓</span>
                        Фрагмент сохранён — для этого предложения записано аудио.
                    </div>
                )}

                <div style={{ marginBottom: '10px', fontSize: '14px' }}>
                    <div style={{ marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <strong>
                            {items.length > 0 && (
                                <>Фрагмент {currentIndex + 1} из {items.length}</>
                            )}
                            {partIndex != null && sentence && (
                                <span style={{ fontWeight: 'normal', color: '#666', fontSize: '13px' }}>
                                    {' '}(предложение {item.sentenceIndex + 1}, часть {partIndex + 1})
                                </span>
                            )}
                        </strong>
                        {hasAudio && (
                            <span
                                style={{
                                    padding: '2px 8px',
                                    background: '#e6f7ff',
                                    color: '#1890ff',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    fontWeight: '500'
                                }}
                            >
                                ✓ Есть аудио
                            </span>
                        )}
                    </div>
                    <div style={{ marginBottom: '6px', whiteSpace: 'pre-wrap' }}>
                        {item?.text}
                    </div>
                    {partIndex != null && sentence?.partsRu && sentence.partsRu[partIndex] && (
                        <div style={{ fontSize: '13px', color: '#666' }}>
                            {sentence.partsRu[partIndex]}
                        </div>
                    )}
                    {partIndex == null && sentence?.ru && (
                        <div style={{ fontSize: '13px', color: '#666' }}>
                            {sentence.ru}
                        </div>
                    )}
                </div>

                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '10px', flexWrap: 'wrap' }}>
                    <div style={{ minWidth: '240px', flex: 1 }}>
                        <label style={{ fontSize: '13px' }}>Начало (сек)</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', marginBottom: '6px', flexWrap: 'wrap' }}>
                            <input
                                type="text"
                                inputMode="decimal"
                                placeholder="0"
                                value={startInput}
                                onChange={(e) => setStartInput(formatSecondsInput(e.target.value))}
                                onFocus={(e) => e.target.value === '0' && e.target.select()}
                                style={{ width: '100px' }}
                            />
                            <button
                                type="button"
                                onClick={() => adjustBoundary('start', -0.1)}
                                style={{ fontSize: '12px' }}
                            >
                                -0.1
                            </button>
                            <button
                                type="button"
                                onClick={() => adjustBoundary('start', 0.1)}
                                style={{ fontSize: '12px' }}
                            >
                                +0.1
                            </button>
                            <button
                                type="button"
                                onClick={setStartFromPlayer}
                                style={{ fontSize: '12px' }}
                            >
                                Начало из позиции
                            </button>
                        </div>
                        <input
                            type="range"
                            min="0"
                            max={duration || 0}
                            step="0.01"
                            value={Math.min(parseFloat(startInput) || 0, duration || 0)}
                            onChange={(e) => setStartInput(String(Number(e.target.value).toFixed(3)))}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div style={{ minWidth: '240px', flex: 1 }}>
                        <label style={{ fontSize: '13px' }}>Конец (сек)</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', marginBottom: '6px', flexWrap: 'wrap' }}>
                            <input
                                type="text"
                                inputMode="decimal"
                                placeholder="0"
                                value={endInput}
                                onChange={(e) => setEndInput(formatSecondsInput(e.target.value))}
                                onFocus={(e) => e.target.value === '0' && e.target.select()}
                                style={{ width: '100px' }}
                            />
                            <button
                                type="button"
                                onClick={() => adjustBoundary('end', -0.1)}
                                style={{ fontSize: '12px' }}
                            >
                                -0.1
                            </button>
                            <button
                                type="button"
                                onClick={() => adjustBoundary('end', 0.1)}
                                style={{ fontSize: '12px' }}
                            >
                                +0.1
                            </button>
                            <button
                                type="button"
                                onClick={setEndFromPlayer}
                                style={{ fontSize: '12px' }}
                            >
                                Конец из позиции
                            </button>
                        </div>
                        <input
                            type="range"
                            min="0"
                            max={duration || 0}
                            step="0.01"
                            value={Math.min(parseFloat(endInput) || 0, duration || 0)}
                            onChange={(e) => setEndInput(String(Number(e.target.value).toFixed(3)))}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <button
                        type="button"
                        onClick={handleSaveSlice}
                        disabled={saving}
                        style={{
                            marginLeft: 'auto',
                            padding: '8px 14px',
                            borderRadius: '6px',
                            border: 'none',
                            background: '#4a90e2',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: '13px'
                        }}
                    >
                        {saving ? 'Сохраняем...' : 'Сохранить фрагмент'}
                    </button>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
                    <button
                        type="button"
                        onClick={() => handleNavigation('prev')}
                        disabled={currentIndex === 0 || saving}
                        style={{
                            padding: '6px 10px',
                            borderRadius: '6px',
                            border: '1px solid #ddd',
                            background: '#f9f9f9',
                            cursor: (currentIndex === 0 || saving) ? 'default' : 'pointer',
                            fontSize: '13px',
                            opacity: (currentIndex === 0 || saving) ? 0.5 : 1
                        }}
                    >
                        ← Предыдущее
                    </button>
                    <button
                        type="button"
                        onClick={() => handleNavigation('next')}
                        disabled={currentIndex === items.length - 1 || saving}
                        style={{
                            padding: '6px 10px',
                            borderRadius: '6px',
                            border: '1px solid #4a90e2',
                            background: '#4a90e2',
                            color: 'white',
                            cursor: (currentIndex === items.length - 1 || saving) ? 'default' : 'pointer',
                            fontSize: '13px',
                            opacity: (currentIndex === items.length - 1 || saving) ? 0.5 : 1
                        }}
                    >
                        Следующее →
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TextBlockEditor;
